import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #sellingPlanLoadingCleanup;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

let swiperThumbs;

function reinintslider() {
  if (swiperThumbs) swiperThumbs.destroy(true, true);

  swiperThumbs = new Swiper(".empty_cart_collaction_swiper", {
    loop: false,
    grabCursor: true,
    spaceBetween: 16,
    slidesPerView: 2.444,
    // slideToClickedSlide: true,
    watchSlidesProgress: true,
    scrollbar: { el: ".swiper-scrollbar" },
    mousewheel: { forceToAxis: true, releaseOnEdges: false, eventsTarget: '.empty_cart_collaction_swiper' },
    breakpoints: { 0: { slidesPerView: 2.001, spaceBetween: 12 }, 750: { slidesPerView: 3.001, spaceBetween: 24 } }
  });
}


function initSliderWhenReady() {
  const checkExist = setInterval(() => {
    const container = document.querySelector(".empty_cart_collaction");
    if (container) {
      reinintslider();
      clearInterval(checkExist); 
    }
  }, 1000); 
}


    
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      
      onAnimationEnd(row, remove);
      initSliderWhenReady();
    });
  }

  /**
   * Handles selling plan selection for a cart line.
   * @param {{ line?: string | number; quantity?: string | number; selling_plan?: string | number } | Event} payloadOrEvent
   * @param {Event} [maybeEvent]
   */
  onSellingPlanSelect(payloadOrEvent, maybeEvent) {
    let data = payloadOrEvent;
    let event = maybeEvent;

    if (!(event instanceof Event) && data instanceof Event) {
      event = data;
      data = undefined;
    }

    if (!(event instanceof Event)) return;

    const button =
      event.target instanceof HTMLElement
        ? event.target.closest('.cart-selling-plan__option, .cart-selling-plan__cta')
        : null;
    if (!button) return;

    event.preventDefault();

    if (!data || typeof data !== 'object') {
      data = {
        line: button.dataset.line,
        quantity: button.dataset.quantity,
        selling_plan: button.dataset.sellingPlan,
      };
    }

    const { line, quantity, selling_plan: sellingPlanRaw } = data ?? {};

    const lineNumber = Number(line);
    if (!Number.isFinite(lineNumber) || lineNumber <= 0) return;

    const quantityNumber = Number(quantity);
    const resolvedQuantity = Number.isFinite(quantityNumber) && quantityNumber > 0 ? quantityNumber : 1;

    let sellingPlanId = null;
    if (sellingPlanRaw !== '' && sellingPlanRaw !== undefined && sellingPlanRaw !== null) {
      const parsedSellingPlan = Number(sellingPlanRaw);
      if (Number.isFinite(parsedSellingPlan)) {
        sellingPlanId = parsedSellingPlan;
      } else {
        return;
      }
    }

    const isOptionButton = button.classList.contains('cart-selling-plan__option');

    if (isOptionButton && button.getAttribute('aria-current') === 'true') {
      button.closest('details')?.removeAttribute('open');
      return;
    }

    button.blur();

    if (isOptionButton) {
      button.parentElement
        ?.querySelectorAll('[aria-current="true"]')
        .forEach((node) => node !== button && node.removeAttribute('aria-current'));
      button.setAttribute('aria-current', 'true');
      button.closest('details')?.removeAttribute('open');
    }

    this.#setSellingPlanLoadingState(button);

    this.updateQuantity({
      line: lineNumber,
      quantity: resolvedQuantity,
      sellingPlanId: sellingPlanId,
      action: 'selling-plan',
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {number | string | null} [config.sellingPlanId] - The selected selling plan identifier, or null to remove it.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const isSellingPlanUpdate = config.action === 'selling-plan';

    this.#disableCartItems();

    const { line, quantity, sellingPlanId } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const payload = {
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };

    if (Object.prototype.hasOwnProperty.call(config, 'sellingPlanId')) {
      payload.selling_plan = sellingPlanId;
    }

    const body = JSON.stringify(payload);

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }
       
        const sectionHtml = parsedResponseText?.sections?.[this.sectionId];

        let newCartItemCount = parsedResponseText?.item_count ?? 0;

        if (sectionHtml) {
          const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');

          // Grab the new cart item count from a hidden element
          const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
          newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : newCartItemCount;
        }
        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        let renderPromise;

        if (sectionHtml) {
          renderPromise = morphSection(this.sectionId, sectionHtml);
        } else {
          renderPromise = sectionRenderer.renderSection(this.sectionId, { cache: false });
        }

        return renderPromise;
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        if (isSellingPlanUpdate) {
          this.#clearSellingPlanLoadingState();
        }
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Shows the loading indicator for selling plan updates.
   * @param {HTMLElement} button - The button that triggered the update.
   */
  #setSellingPlanLoadingState(button) {
    if (!(button instanceof HTMLElement)) return;

    const sellingPlanContainer = button.closest('.cart-selling-plan');

    if (sellingPlanContainer instanceof HTMLElement) {
      sellingPlanContainer.classList.add('cart-selling-plan--loading');
      this.#sellingPlanLoadingCleanup = () => {
        sellingPlanContainer.classList.remove('cart-selling-plan--loading');
      };
      return;
    }

    if (button.classList.contains('cart-selling-plan__cta')) {
      button.classList.add('cart-selling-plan__cta--loading');
      this.#sellingPlanLoadingCleanup = () => {
        button.classList.remove('cart-selling-plan__cta--loading');
      };
    }
  }

  /**
   * Hides the loading indicator for selling plan updates.
   */
  #clearSellingPlanLoadingState() {
    if (typeof this.#sellingPlanLoadingCleanup === 'function') {
      this.#sellingPlanLoadingCleanup();
    }
    this.#sellingPlanLoadingCleanup = undefined;
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
